// GENERATED COPY. Edit shared/security only; policyParity.test.ts enforces parity.
/**
 * @fileoverview Password policy for platform account credentials (CIB-1166).
 *
 * Implements SILIA-SGSI-PO-008 v1.0 §5.1–5.2 for passwords that are being SET:
 * registration, reset and invitation activation, and the admin user update.
 * Login never evaluates it, so existing passwords keep working even when they
 * predate the policy.
 *
 * Two kinds of rule, reported in this order:
 * - `include` — what the password must have: 8 to 1024 characters (code
 *   points; the ceiling only bounds what a check can cost), A–Z, a–z, 0–9 and a
 *   special character (anything that is not a letter or a number, in any
 *   script: space, `_` and emoji count; `ñ` does not).
 * - `avoid` — what it must not have: personal data (any word of the name, the
 *   email local part or its pieces), sequences and repeats of 4 (1234, 9876,
 *   abcd, aaaa, and keyboard rows/columns like qwerty, asdf, 1qaz) and the
 *   blocklist (common passwords, "silia", "kivo").
 *
 * Everything is compared case- and accent-insensitively, and the personal-data
 * and blocklist rules also undo leetspeak, so «C0ll@do» matches «Collado».
 *
 * Every check is linear in its input, and the API rejects anything over the
 * ceiling before running one: whatever a caller sends, checking it is cheap.
 *
 * Framework-free on purpose: the Lambdas enforce it and the web app renders it
 * as a live checklist. The rules mirror `src/data/passwordPolicy.ts` of the
 * Design prototype; the UI copy lives in the app, only the API messages here.
 *
 * The web app cannot import `shared/` (separate build), so this file and
 * `commonPasswords.ts` are copied verbatim to
 * `app/src/features/password-policy/policy/`. Edit both copies together:
 * `app/src/features/password-policy/__tests__/policyParity.test.ts` fails when
 * they differ.
 *
 * @module shared/security/passwordPolicy
 *
 * @example
 * import { validatePassword } from '@shared/security/passwordPolicy';
 *
 * validatePassword('Mt7#vRq2Lp', { name: 'Ana López', email: 'ana@example.com' }); // null
 * validatePassword('Silia2026!', { name: 'Ana López', email: 'ana@example.com' })?.rule; // 'blocklist'
 */

import { COMMON_BASE_WORDS, COMMON_PASSWORDS } from './commonPasswords';

export const PASSWORD_MIN_LENGTH = 8;

/** Ceiling on a password. Far above what anyone types or a manager generates
 *  (NIST SP 800-63B asks to accept at least 64): it exists so that checking a
 *  password costs a bounded amount, since the API checks whatever it is sent. */
export const PASSWORD_MAX_LENGTH = 1024;

/** Shortest sequence or repeat that is rejected: «1234» is, «123» is not. */
export const PASSWORD_SEQUENCE_MIN = 4;

/** Words that may not appear anywhere in the password. */
export const PASSWORD_BANNED_WORDS: readonly string[] = ['silia', 'kivo'];

/** Shorter pieces of a name or email are not personal data: without this
 *  floor, a two-letter name would veto half of the possible passwords. */
const PERSONAL_TOKEN_MIN = 3;

/** Only the start of a name or email is compared, for the same reason as
 *  `PASSWORD_MAX_LENGTH`: the identity comes from the request too, and no real
 *  name runs this long. */
const IDENTITY_MAX_LENGTH = 256;

export type PasswordRuleId =
  | 'length'
  | 'upper'
  | 'lower'
  | 'number'
  | 'special'
  | 'personal'
  | 'sequence'
  | 'blocklist';

export type PasswordRuleKind = 'include' | 'avoid';

export interface PasswordRule {
  id: PasswordRuleId;
  kind: PasswordRuleKind;
}

/** In reading (and reporting) order: first what to include, then what to avoid. */
export const PASSWORD_RULES: readonly PasswordRule[] = [
  { id: 'length', kind: 'include' },
  { id: 'upper', kind: 'include' },
  { id: 'lower', kind: 'include' },
  { id: 'number', kind: 'include' },
  { id: 'special', kind: 'include' },
  { id: 'personal', kind: 'avoid' },
  { id: 'sequence', kind: 'avoid' },
  { id: 'blocklist', kind: 'avoid' },
];

/** What broke an `avoid` rule, so a UI can say what to change. */
export type PasswordAvoidReason =
  | 'name'
  | 'email'
  | 'repeat'
  | 'consecutive'
  | 'keyboard'
  | 'common'
  | 'banned';

/** What the platform knows about the account: the full `name` (the User table
 *  has no separate last name) and the email. Missing values are skipped, and so
 *  is anything that is not a string: the identity comes from request bodies. */
export interface PasswordIdentity {
  name?: string | null;
  email?: string | null;
}

export interface PasswordRuleResult {
  ok: boolean;
  reason?: PasswordAvoidReason;
}

export type PasswordCheck = Record<PasswordRuleId, PasswordRuleResult>;

/* ─── Normalisation ─────────────────────────────────────────────────────── */

const stripAccents = (s: string) => s.normalize('NFD').replace(/\p{M}+/gu, '');

/** Lower-cased and without accents: how everything is compared. */
const fold = (s: string) => stripAccents(s).toLowerCase();

const LEET: Readonly<Record<string, string>> = {
  '@': 'a',
  '4': 'a',
  '0': 'o',
  '3': 'e',
  $: 's',
  '5': 's',
  '7': 't',
  '!': 'i',
};

/** Undoes leetspeak. «1» stands for either «i» or «l», so there are two readings. */
function unleet(s: string): string[] {
  const base = Array.from(s, c => LEET[c] ?? c).join('');
  return [base.replace(/1/g, 'i'), base.replace(/1/g, 'l')];
}

/* ─── Rules ─────────────────────────────────────────────────────────────── */

interface PersonalToken {
  token: string;
  source: 'name' | 'email';
}

/** A name or email as the policy reads it: strings only, and only their start. */
const identityText = (value: unknown): string =>
  typeof value === 'string' ? value.slice(0, IDENTITY_MAX_LENGTH) : '';

function personalTokens(identity: PasswordIdentity): PersonalToken[] {
  const out: PersonalToken[] = [];
  // Name first: when the email local part is the name, the name is what to change.
  identityText(identity.name)
    .split(/[\s\-'’.]+/)
    .forEach(part => {
      const token = fold(part);
      if (token.length >= PERSONAL_TOKEN_MIN) out.push({ token, source: 'name' });
    });
  const local = fold(identityText(identity.email).split('@')[0]);
  if (local.length >= PERSONAL_TOKEN_MIN) out.push({ token: local, source: 'email' });
  local.split(/[._\-+]+/).forEach(part => {
    if (part.length >= PERSONAL_TOKEN_MIN && part !== local)
      out.push({ token: part, source: 'email' });
  });
  return out;
}

function checkPersonal(folded: string, identity: PasswordIdentity): PasswordRuleResult {
  const readings = [folded, ...unleet(folded)];
  const match = personalTokens(identity).find(({ token }) => readings.some(r => r.includes(token)));
  return match ? { ok: false, reason: match.source } : { ok: true };
}

/** QWERTY rows and columns, plus the shifted number row. Any stretch of
 *  `PASSWORD_SEQUENCE_MIN` keys of one of them, forwards or backwards. */
const KEYBOARD_RUNS: readonly string[] = [
  '`1234567890-=',
  '~!@#$%^&*()_+',
  'qwertyuiop[]\\',
  "asdfghjkl;'",
  'zxcvbnm,./',
  '1qaz',
  '2wsx',
  '3edc',
  '4rfv',
  '5tgb',
  '6yhn',
  '7ujm',
  '8ik,',
  '9ol.',
  '0p;/',
];

const KEYBOARD_WINDOWS: readonly string[] = (() => {
  const out = new Set<string>();
  KEYBOARD_RUNS.forEach(run => {
    [run, Array.from(run).reverse().join('')].forEach(r => {
      for (let i = 0; i + PASSWORD_SEQUENCE_MIN <= r.length; i += 1) {
        out.add(r.slice(i, i + PASSWORD_SEQUENCE_MIN));
      }
    });
  });
  return Array.from(out);
})();

function checkSequence(folded: string): PasswordRuleResult {
  const chars = Array.from(folded);
  // Repeat: the same character 4 times in a row.
  let run = 1;
  for (let i = 1; i < chars.length; i += 1) {
    run = chars[i] === chars[i - 1] ? run + 1 : 1;
    if (run >= PASSWORD_SEQUENCE_MIN) return { ok: false, reason: 'repeat' };
  }
  // Consecutive: 4 letters or 4 digits going up or down one by one.
  const kind = (c: string) => {
    if (/[a-z]/.test(c)) return 'letter';
    return /[0-9]/.test(c) ? 'digit' : null;
  };
  for (let i = 0; i + PASSWORD_SEQUENCE_MIN <= chars.length; i += 1) {
    const win = chars.slice(i, i + PASSWORD_SEQUENCE_MIN);
    const k = kind(win[0]);
    if (k && win.every(c => kind(c) === k)) {
      const steps = win.slice(1).map((c, j) => c.charCodeAt(0) - win[j].charCodeAt(0));
      if (steps.every(s => s === 1) || steps.every(s => s === -1)) {
        return { ok: false, reason: 'consecutive' };
      }
    }
  }
  // Keyboard: stretches of one row or one column.
  if (KEYBOARD_WINDOWS.some(w => folded.includes(w))) return { ok: false, reason: 'keyboard' };
  return { ok: true };
}

const CODE_LOWER_A = 'a'.charCodeAt(0);
const CODE_LOWER_Z = 'z'.charCodeAt(0);
const isAsciiLower = (code: number) => code >= CODE_LOWER_A && code <= CODE_LOWER_Z;

/**
 * The core of the password: without the digits and symbols at both ends.
 * A scan rather than `/[^a-z]+$/`, which backtracks quadratically on a long run
 * of symbols followed by a letter: the API evaluates whatever it is sent.
 */
function core(folded: string): string {
  let start = 0;
  let end = folded.length;
  while (start < end && !isAsciiLower(folded.charCodeAt(start))) start += 1;
  while (end > start && !isAsciiLower(folded.charCodeAt(end - 1))) end -= 1;
  return folded.slice(start, end);
}

function checkBlocklist(folded: string): PasswordRuleResult {
  const readings = [folded, ...unleet(folded)];
  if (PASSWORD_BANNED_WORDS.some(w => readings.some(r => r.includes(w)))) {
    return { ok: false, reason: 'banned' };
  }
  if (COMMON_PASSWORDS.has(folded)) return { ok: false, reason: 'common' };
  const c = core(folded);
  if (c && unleet(c).some(r => COMMON_BASE_WORDS.has(r) || COMMON_PASSWORDS.has(r))) {
    return { ok: false, reason: 'common' };
  }
  // Leetspeak inside the core («p@ssw0rd»): undone before trimming.
  if (unleet(folded).some(r => COMMON_BASE_WORDS.has(core(r))))
    return { ok: false, reason: 'common' };
  return { ok: true };
}

/**
 * Evaluates the eight rules. An empty password breaks every `include` rule and
 * no `avoid` rule: there is nothing to avoid yet.
 */
export function checkPassword(password: string, identity: PasswordIdentity = {}): PasswordCheck {
  const folded = fold(password);
  const hasInput = password.length > 0;
  const { length } = Array.from(password);
  return {
    length: { ok: length >= PASSWORD_MIN_LENGTH && length <= PASSWORD_MAX_LENGTH },
    // A–Z and a–z as the policy says: «Á» and «ñ» do not count.
    upper: { ok: /[A-Z]/.test(password) },
    lower: { ok: /[a-z]/.test(password) },
    number: { ok: /[0-9]/.test(password) },
    // Not a letter or a number in any script. A combining accent belongs to
    // its letter, so a decomposed «é» is not a special character either.
    special: { ok: /[^\p{L}\p{M}\p{N}]/u.test(password) },
    personal: hasInput ? checkPersonal(folded, identity) : { ok: true },
    sequence: hasInput ? checkSequence(folded) : { ok: true },
    blocklist: hasInput ? checkBlocklist(folded) : { ok: true },
  };
}

export const isPasswordValid = (check: PasswordCheck): boolean =>
  PASSWORD_RULES.every(rule => check[rule.id].ok);

/** The first rule the password breaks, in `PASSWORD_RULES` order. */
export const firstFailedRule = (check: PasswordCheck): PasswordRuleId | undefined =>
  PASSWORD_RULES.find(rule => !check[rule.id].ok)?.id;

/* ─── API contract ──────────────────────────────────────────────────────── */

/** `code` of the 400 body when a password is rejected, next to `rule` and `message`. */
export const PASSWORD_POLICY_ERROR_CODE = 'PASSWORD_POLICY_VIOLATION';

/** The avoid rules share one message (CIB-1166 §B): the API does not say which
 *  personal data, pattern or list matched. */
const AVOID_MESSAGE =
  "The password doesn't meet the security policy. Avoid personal data, sequences or common passwords.";

export const PASSWORD_POLICY_MESSAGES: Readonly<Record<PasswordRuleId, string>> = {
  length: `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`,
  upper: 'Password must include an uppercase letter (A–Z).',
  lower: 'Password must include a lowercase letter (a–z).',
  number: 'Password must include a number (0–9).',
  special: 'Password must include a special character, like ! @ # $.',
  personal: AVOID_MESSAGE,
  sequence: AVOID_MESSAGE,
  blocklist: AVOID_MESSAGE,
};

/** The `length` message for a password over `PASSWORD_MAX_LENGTH`. */
const TOO_LONG_MESSAGE = `Password must be at most ${PASSWORD_MAX_LENGTH} characters.`;

export interface PasswordPolicyViolation {
  code: typeof PASSWORD_POLICY_ERROR_CODE;
  rule: PasswordRuleId;
  message: string;
}

const violationOf = (
  rule: PasswordRuleId,
  message: string = PASSWORD_POLICY_MESSAGES[rule]
): PasswordPolicyViolation => ({ code: PASSWORD_POLICY_ERROR_CODE, rule, message });

/** Over the ceiling. Code units bound code points from above by two, so a
 *  huge string is caught before it is ever split into code points. */
const isOverMaxLength = (password: string): boolean =>
  password.length > PASSWORD_MAX_LENGTH * 2 || Array.from(password).length > PASSWORD_MAX_LENGTH;

/**
 * Server-side gate for a password that is about to be set. Returns `null` when
 * it meets the policy, or the violation to send back as the 400 body. Anything
 * that is not a string fails as too short, and anything over
 * `PASSWORD_MAX_LENGTH` as too long, before any other rule runs. The password
 * itself is never part of the result, so the result is safe to log.
 */
export function validatePassword(
  password: unknown,
  identity: PasswordIdentity = {}
): PasswordPolicyViolation | null {
  if (typeof password !== 'string') return violationOf('length');
  if (isOverMaxLength(password)) return violationOf('length', TOO_LONG_MESSAGE);
  const rule = firstFailedRule(checkPassword(password, identity));
  return rule ? violationOf(rule) : null;
}
